import { isExternal, isCouponAvailableAtDate, isCouponExpired, isLinked } from 'mobx/Coupons/utils'
import { groupBy } from 'lodash-es'
import { action, computed, observable, toJS } from 'mobx'
import type { PrbCoupon, Filter, CouponsDependencies } from './types'
import { CouponFilter } from './types'
import type { LocalizedCoupon } from 'types/Coupons'
import { Channel , CouponSource, CouponStatus } from 'shared-types/Coupon'
import type { Coupon, LinkedCoupon, NonExternalCoupon, OrderType } from 'shared-types/Coupon'
import { getTranslatedTextByKey, type LanguageLocale } from 'utils/language'
import CouponError, { ErrorCode } from './errors'
import { SnackbarStatus } from 'mobx/Infra/Infra.type'

export default class CouponsStore {
	private readonly dependencies: CouponsDependencies

	constructor(dependencies: CouponsDependencies) {
		this.dependencies = dependencies
	}

	@observable coupons: Coupon[] = []

	/**
	 * When not null, opens a pop-up of a coupon modal with details about a single coupon
	 */
	@observable couponModal: Coupon | null = null

	@observable private readonly externalCouponsCache = new Map<string, PrbCoupon>()

	@action
	setCouponModal(coupon: Coupon | null) {
		console.log('modal:', toJS(coupon))
		this.couponModal = coupon
	}

	@action
	async setAppliedCoupons(coupons: NonExternalCoupon[]): Promise<void> {
		const cachedCouponsMap = new Map<string, Coupon>()

		this.coupons.forEach((coupon) => {
			if (!isExternal(coupon)) {
				cachedCouponsMap.set(coupon.code, coupon)
			}
		})

		const incomingCouponCodes = new Set(coupons.map((coupon) => coupon.code))

		coupons.forEach((incomingCoupon) => {
			if (incomingCoupon.expiration) {
				incomingCoupon.expiration = new Date(incomingCoupon.expiration)
			}

			if (isLinked(incomingCoupon) && incomingCoupon.attachedAt) {
				incomingCoupon.attachedAt = new Date(incomingCoupon.attachedAt)
			}

			const existingCoupon = cachedCouponsMap.get(incomingCoupon.code)

			if (existingCoupon) {
				existingCoupon.flags = { ...existingCoupon.flags, ...incomingCoupon.flags }
				existingCoupon.price = incomingCoupon.price
			} else {
				this.coupons.push(incomingCoupon)
			}
		})

		this.coupons.forEach((existingCoupon) => {
			if (!isExternal(existingCoupon) && !incomingCouponCodes.has(existingCoupon.code)) {
				existingCoupon.flags = {
					...existingCoupon.flags,
					applied: { value: false },
					active: { value: false },
				}
			}
		})
	}

	private mergeCoupon(coupon: Coupon): void {
		const cachedCoupon = isExternal(coupon)
			? this.coupons.find((c) => isExternal(coupon) && coupon.id === c.id)
			: this.internalCoupons.find(({ id, code }) => id === coupon.id && code === coupon.code)

		if (!cachedCoupon) {
			return
		}

		coupon.flags.applied = { value: !!cachedCoupon?.flags.applied?.value }
		coupon.flags.active = {
			value: !!cachedCoupon?.flags.active?.value,
			reasonCode: cachedCoupon?.flags.active?.reasonCode,
		}
	}

	@computed get appliedCoupons(): NonExternalCoupon[] {
		return this.coupons.filter((coupon) => coupon.flags.applied?.value) as NonExternalCoupon[]
	}

	@computed get appliedCouponsWithoutGovernment(): Coupon[] {
		return this.coupons.filter((coupon) => !isExternal(coupon) && coupon.flags.applied?.value && coupon.code !== 'GOVERNMENT')
	}

	private prepareCouponsToAdd(cachedCoupons: Coupon[], newCoupons: Coupon[], sources: CouponSource[]): Coupon[] {
		newCoupons.forEach((coupon) => this.mergeCoupon(coupon))
		const ignoreOrderTypes = this.dependencies.getIgnoreOrderTypes()

		return cachedCoupons
			.filter(({ id, flags, source }) => {
				const isRelevantSource = sources.includes(source)
				const shouldShowInWallet = flags.wallet?.value || flags.showInMyCoupons?.value
				const newIdAppearsInNewCoupons = newCoupons.some(({ id: newId }) => id === newId)

				return !isRelevantSource || (!shouldShowInWallet && !newIdAppearsInNewCoupons)
			})
			.concat(newCoupons)
			.flatMap(({ orderTypes, ...rest }) => {
				const newOrderTypes = orderTypes.filter((f) => !ignoreOrderTypes.includes(f))
				if (!newOrderTypes.length) {
					return []
				}

				return { ...rest, orderTypes: newOrderTypes }
			})
	}

	@action
	public async fetchInternalCoupons(): Promise<void> {
		this.coupons = await this.dependencies
			.getInternalCoupons()
			.then((coupons) => this.prepareCouponsToAdd(this.coupons, coupons, [CouponSource.INDIVIDUAL, CouponSource.INTERNAL, CouponSource.LINKED]))
	}

	@action
	public async fetchExternalCoupons(): Promise<void> {
		this.coupons = await this.dependencies
			.getExternalCoupons()
			.then((coupons) => this.prepareCouponsToAdd(this.coupons, coupons, [CouponSource.EXTERNAL]))
	}

	@computed private get internalCoupons(): NonExternalCoupon[] {
		return this.coupons.filter((c) => !isExternal(c)) as NonExternalCoupon[]
	}

	private getCachedCoupon(code: string): Coupon | null {
		const coupon = this.internalCoupons.find(({ code: id }) => id === code) ?? null

		if (!coupon) {
			return null
		}

		return coupon
	}

	@action
	clearCoupons(): void {
		this.coupons = []
	}

	@action
	async getCoupon(code: string): Promise<Coupon | null> {
		const cachedCoupon = this.getCachedCoupon(code)
		if (cachedCoupon) {
			return cachedCoupon
		}

		const coupon = await this.dependencies.getCoupon(code)

		if (!coupon) {
			return null
		}

		if (isLinked(coupon)) {
			const couponOfSameParent = this.coupons.find((c) => isLinked(c) && c.parentCode === coupon.parentCode)
			if (couponOfSameParent) {
				this.dependencies.showSnackbar({
					snackId: '',
					message: getTranslatedTextByKey(
						'eCommerce.coupons.snackbar.couponAlreadyExists',
						'There is already an individual coupon of that parent'
					),
					status: SnackbarStatus.INFO,
					isAttachedToElement: false,
					duration: 5000,
				})
				return couponOfSameParent
			}
		}

		this.mergeCoupon(coupon)
		this.coupons = this.coupons
			.filter(
				(c) =>
					isExternal(coupon) !== isExternal(c) || (isExternal(coupon) ? c.id !== coupon.id : (c as NonExternalCoupon).code !== coupon.code)
			)
			.concat(coupon)

		return this.coupons.at(-1)!
	}

	static validateCoupon(coupon: Coupon): void {
		if (isCouponExpired(coupon)) {
			throw new CouponError('Coupon is expired', ErrorCode.COUPON_EXPIRED)
		}

		if (!isExternal(coupon) && coupon.status === CouponStatus.REDEEMED) {
			throw new CouponError('Coupon is redeemed', ErrorCode.COUPON_REDEEMED)
		}

		if (isLinked(coupon) && coupon.usesLeft != null && coupon.usesLeft <= 0) {
			throw new CouponError('No more uses left', ErrorCode.COUPON_REDEEMED)
		}
	}

	private async normaliseAddToWalletError(code: string, error: CouponError): Promise<CouponError> {
		const valdiateAndReturn = async (): Promise<CouponError> => {
			const coupon = await this.getCoupon(code)

			if (!coupon) {
				return new CouponError('Coupon not found while normalising error', ErrorCode.COUPON_NOT_FOUND)
			}

			try {
				CouponsStore.validateCoupon(coupon)
				return error
			} catch (err) {
				return err as CouponError
			}
		}

		switch (error.code) {
			case ErrorCode.USERS_COUPON_INDIVIDUAL_COUPON_NOT_AVAILABLE: {
				return valdiateAndReturn()
			}

			case ErrorCode.USERS_COUPON_INDIVIDUAL_COUPON_EXISTS:
			case ErrorCode.USERS_COUPON_INDIVIDUAL_PARENT_COUPON_EXISTS: {
				const newErr = await valdiateAndReturn()
				return new CouponError(error.message, error.code, newErr === error)
			}

			default:
				return error
		}
	}

	@action
	async linkCoupon(code: string): Promise<LinkedCoupon> {
		const coupon = await this.dependencies.linkCoupon(code).catch(async (error: CouponError) => {
			throw await this.normaliseAddToWalletError(code, error)
		})

		this.coupons = this.coupons.filter((c) => isExternal(c) || c.code !== code).concat(coupon)
		return coupon
	}

	private readonly filterMap: Partial<Record<Filter, (coupon: Coupon) => boolean>> = {
		[CouponFilter.ALL]: ({ flags: { wallet, showInMyCoupons }, source }) => !!wallet?.value || !!showInMyCoupons?.value || isLinked({ source }),
		[CouponFilter.KIOSK]: (coupon: Coupon) => !!coupon?.channels?.includes(Channel.KIOSK),
		[CouponFilter.MY_COUPONS]: (coupon) => {
			const { source, flags } = coupon
			if (!flags) {
				console.error('Flags doesnt exist for', toJS(coupon))
				return false
			}
			return (isLinked({ source }) || !!flags.showInMyCoupons?.value) && this.dependencies.isUserLoggedIn()
		},
	}

	assertCouponAvailable(coupon: Coupon, { date, channel, orderType }: { date: Date; channel: Channel; orderType: OrderType }): void {
		if (!isCouponAvailableAtDate(coupon, date)) {
			throw new CouponError('Coupon not available at date', ErrorCode.COUPON_NOT_AVAILABLE_FOR_TIME)
		}

		if (!isExternal(coupon) && !coupon.channels.includes(channel)) {
			throw new CouponError('Coupon not available for channel', ErrorCode.COUPON_NOT_AVAILABLE_FOR_CHANNEL)
		}

		if (!coupon.orderTypes.includes(orderType) && this.dependencies.isLocalized()) {
			throw new CouponError('Coupon not available for order type', ErrorCode.COUPON_NOT_AVAILABLE_FOR_ORDER_TYPE)
		}
	}

	filterCoupons(filter: Filter): Coupon[] {
		const defaultFilter = ({ orderTypes }: Coupon) => orderTypes.includes(filter as OrderType)

		const result = this.coupons
			.filter(
				(coupon) =>
					(this.filterMap[filter] ?? defaultFilter)(coupon) &&
					(!isLinked(coupon) || coupon.status === CouponStatus.AVAILABLE || coupon.flags.applied?.value) &&
					!isCouponExpired(coupon)
			)
			.sort((a, b) => {
				const aIsExternal = isExternal(a)
				const bIsExternal = isExternal(b)

				if (!aIsExternal || !bIsExternal) {
					return aIsExternal ? -1 : 1
				}

				if (!a.sequence || Number.isNaN(+a.sequence)) {
					if (!b.sequence || Number.isNaN(+b.sequence)) {
						return 0
					}

					return 1
				}

				if (!b.sequence || Number.isNaN(+b.sequence)) {
					return -1
				}

				return a.sequence - b.sequence
			})

		// Make sure parent and child don't both appear
		return result.filter(
			(coupon, _, array) => isExternal(coupon) || isLinked(coupon) || array.every((c) => !isLinked(c) || c.parentCode !== coupon.code)
		)
	}

	getGroupedCoupons(filter: Filter): Record<Filter, Coupon[]> {
		const grouped = groupBy(this.filterCoupons(filter), ({ orderTypes: type }) => (filter === CouponFilter.ALL ? type.join() : filter)) as Record<
			Filter,
			Coupon[]
		>

		;(Object.keys(grouped) as (keyof typeof grouped)[]).forEach((key) =>
			grouped[key].sort((a, b) => {
				const aDate: number = (isLinked(a) && a.attachedAt?.getTime()) || Infinity
				const bDate: number = (isLinked(b) && b.attachedAt?.getTime()) || Infinity

				return bDate - aDate
			})
		)

		return grouped
	}

	static localizeCoupon(coupon: Coupon, locale: LanguageLocale): LocalizedCoupon {
		// return addVirtualFields({ ...coupon, title: coupon.title[locale], description: coupon.description[locale] })
		return { ...coupon, title: coupon.title[locale], description: coupon.description[locale] }
	}

	async getPrbCode(id: string): Promise<PrbCoupon> {
		const cachedCoupon = this.externalCouponsCache.get(id)

		if (cachedCoupon) {
			return cachedCoupon
		}

		return this.dependencies.getPrbCode(id).then((result) => {
			this.externalCouponsCache.set(id, { ...result })
			return result
		})
	}
}
